import { DocumentOrientation, TemplateConfig } from "../drawer/types";
import { PublishedFormat, RuntimeFormatConfig } from "./types";

export const MM_TO_PX = 3.7795;

export const mmToDisplayPx = (mm: number): number => Math.round(mm * MM_TO_PX);

const formatMmLabel = (mm: number): string => `${Math.round(mm * 100) / 100}`;

// Placeholder assets for codes with no hardcoded entry; consumers must tolerate empty strings.
const EMPTY_ASSETS: Omit<TemplateConfig, "name" | "widthInMm" | "heightInMm" | "displayWidth" | "displayHeight"> = {
  flyerTemplate: "",
  template: "",
  printingRulesMm: "",
  printingRulesInches: "",
  example: "",
  preview: "",
  placeholder: "",
  frontSide: "",
  backSide: "",
  previewPadding: 0,
  verticalArrowHeight: 0,
  horizontalArrowWidth: 0,
  horizontalArrow: "",
  verticalArrow: "",
  defaultOrientation: DocumentOrientation.Portrait,
};

const LEGACY_BLEED_MM = 5;
const LEGACY_SAFE_AREA_MM = 3.2;
const LEGACY_PORTRAIT_ONLY_CODES = new Set(["TEARAWAY", "DOOR_HANGER"]);

const fromGeometryOrientation = (published: PublishedFormat): DocumentOrientation =>
  published.widthMm > published.heightMm ? DocumentOrientation.Landscape : DocumentOrientation.Portrait;

// Legacy codes keep their hardcoded geometry, bleed, safety and orientation (existing designs were laid out on them)
// and take only availability and template URLs from the API; codes unknown to FORMATS take the API geometry.
export function mapPublishedFormat(published: PublishedFormat, legacy?: TemplateConfig): RuntimeFormatConfig {
  const { templates } = published;
  const templateFields = (base: TemplateConfig) => ({
    printingRulesMm: templates.printingRulesMm ?? base.printingRulesMm,
    printingRulesInches: templates.printingRulesIn ?? base.printingRulesInches,
    flyerTemplate: templates.artworkPdf ?? base.flyerTemplate,
  });

  if (legacy) {
    return {
      ...legacy,
      ...templateFields(legacy),
      name: published.name,
      format: published.code,
      perSideBleed: false,
      bleedMm: LEGACY_BLEED_MM,
      safeAreaMm: LEGACY_SAFE_AREA_MM,
      bleedPx: LEGACY_BLEED_MM * MM_TO_PX,
      safetyPx: LEGACY_SAFE_AREA_MM * MM_TO_PX,
      allowLandscape: !LEGACY_PORTRAIT_ONLY_CODES.has(published.code),
      shapeVariant: published.shapeVariant,
      foldType: "none",
      panelWidthsMm: [],
      channels: published.channels,
      templates,
    };
  }

  const displayWidth = mmToDisplayPx(published.widthMm);
  const displayHeight = mmToDisplayPx(published.heightMm);
  const base: TemplateConfig = {
    ...EMPTY_ASSETS,
    name: published.name,
    widthInMm: published.widthMm,
    heightInMm: published.heightMm,
    displayWidth,
    displayHeight,
  };

  return {
    ...base,
    ...templateFields(base),
    format: published.code,
    width: displayWidth,
    height: displayHeight,
    size: `${formatMmLabel(published.widthMm)} x ${formatMmLabel(published.heightMm)}`,
    measuring: "mm",
    defaultOrientation: fromGeometryOrientation(published),
    perSideBleed: true,
    bleedMm: published.bleedMm,
    safeAreaMm: published.safeAreaMm,
    bleedPx: published.bleedMm * MM_TO_PX,
    safetyPx: published.safeAreaMm * MM_TO_PX,
    allowLandscape: published.allowLandscape,
    shapeVariant: published.shapeVariant,
    foldType: published.foldType,
    panelWidthsMm: published.panelWidthsMm,
    channels: published.channels,
    templates,
  };
}
